import React, { useState, useMemo, useCallback } from 'react';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  Eye,
  ShieldAlert,
  AlertOctagon,
  FileText,
  ChevronDown,
  ChevronRight,
  Phone,
  Play,
  ExternalLink,
  Clock,
  User,
  MessageSquare,
} from 'lucide-react';
import { useSafetyIncidentStats, useSafetyIncidents } from '../hooks/useSafetyIncidents';
import { safetyIncidentsService } from '../services/safetyIncidents';
import type {
  SafetyIncidentItem,
  SafetyIncidentDetail,
  SafetyIncidentStatus,
  SafetyIncidentCategory,
  SafetyIncidentResolution,
  SafetyFlag,
  UpdateSafetyIncidentBody,
} from '../types/safetyIncidents';
import { cn, formatDateTime, formatRelativeTime, formatNumber, showToast } from '../utils/helpers';
import { Badge } from '../components/ui';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: SafetyIncidentStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'FALSE_POSITIVE', label: 'False Positive' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'ESCALATED', label: 'Escalated' },
];

const CATEGORY_OPTIONS: { value: SafetyIncidentCategory | ''; label: string }[] = [
  { value: '', label: 'All Categories' },
  { value: 'THREAT', label: 'Threat' },
  { value: 'SELF_HARM', label: 'Self Harm' },
  { value: 'HARASSMENT', label: 'Harassment' },
  { value: 'ABUSE', label: 'Abuse' },
  { value: 'EXPLOITATION', label: 'Exploitation' },
  { value: 'FRAUD', label: 'Fraud' },
  { value: 'INAPPROPRIATE_CONTENT', label: 'Inappropriate Content' },
  { value: 'OTHER', label: 'Other' },
];

const RESOLUTION_OPTIONS: { value: SafetyIncidentResolution | ''; label: string }[] = [
  { value: '', label: 'Select resolution...' },
  { value: 'CUSTOMER_CONTACTED', label: 'Customer Contacted' },
  { value: 'NO_ACTION_NEEDED', label: 'No Action Needed' },
  { value: 'USER_BANNED', label: 'User Banned' },
  { value: 'USER_WARNED', label: 'User Warned' },
  { value: 'PROVIDER_BANNED', label: 'Provider Banned' },
  { value: 'PROVIDER_WARNED', label: 'Provider Warned' },
  { value: 'ESCALATED_TO_LAW_ENFORCEMENT', label: 'Escalated to Law Enforcement' },
  { value: 'OTHER', label: 'Other' },
];

// ============================================================================
// Badge helpers
// ============================================================================

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'muted' | 'default';

function statusBadgeVariant(status: SafetyIncidentStatus): BadgeVariant {
  const map: Record<SafetyIncidentStatus, BadgeVariant> = {
    OPEN: 'error',
    UNDER_REVIEW: 'info',
    CONFIRMED: 'warning',
    FALSE_POSITIVE: 'muted',
    RESOLVED: 'success',
    ESCALATED: 'error',
  };
  return map[status] || 'muted';
}

function categoryBadgeVariant(category: SafetyIncidentCategory): BadgeVariant {
  const map: Record<SafetyIncidentCategory, BadgeVariant> = {
    THREAT: 'error',
    SELF_HARM: 'error',
    HARASSMENT: 'warning',
    ABUSE: 'error',
    EXPLOITATION: 'error',
    FRAUD: 'warning',
    INAPPROPRIATE_CONTENT: 'muted',
    OTHER: 'muted',
  };
  return map[category] || 'muted';
}

function severityBadgeVariant(severity: string): BadgeVariant {
  const map: Record<string, BadgeVariant> = {
    RED: 'error',
    ORANGE: 'warning',
    YELLOW: 'info',
  };
  return map[severity] || 'muted';
}

function formatStatusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

function formatCategoryLabel(category: string): string {
  return category.replace(/_/g, ' ');
}

// ============================================================================
// Stat Card
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, color, bgColor, loading }) => (
  <div className="card p-4 overflow-hidden">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <p className="text-xl font-bold text-foreground truncate">{value}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// Detail helpers
// ============================================================================

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="pt-2 mt-2 first:mt-0 first:pt-0 overflow-hidden">
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 min-w-0">{children}</div>
  </div>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5 min-w-0">
      <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">{label}</span>
      <span className="text-[10px] text-foreground text-right break-all truncate max-w-[60%]">{value}</span>
    </div>
  );
};

// ============================================================================
// Pagination
// ============================================================================

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}> = ({ page, totalPages, total, onPageChange, loading }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
      <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({formatNumber(total)} total)</p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1 || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronRight className="h-4 w-4 text-foreground rotate-180" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) pageNum = i + 1;
          else if (page <= 3) pageNum = i + 1;
          else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
          else pageNum = page - 2 + i;
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={cn('w-8 h-8 text-xs rounded-lg transition-colors', pageNum === page ? 'bg-primary/20 text-primary font-semibold' : 'text-muted-foreground hover:bg-primary/10')}
            >
              {pageNum}
            </button>
          );
        })}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Flag Card (used in expanded detail)
// ============================================================================

const FlagCard: React.FC<{ flag: SafetyFlag }> = ({ flag }) => (
  <div className="bg-background/50 border border-border rounded-lg p-2.5 space-y-1">
    <div className="flex items-center gap-2">
      <Badge variant={severityBadgeVariant(flag.severity)} size="sm">{flag.severity}</Badge>
      <span className="text-[10px] text-muted-foreground">{formatCategoryLabel(flag.category)}</span>
      <span className="text-[10px] text-muted-foreground ml-auto">{formatRelativeTime(flag.createdAt)}</span>
    </div>
    {flag.message && (
      <p className="text-[11px] text-foreground">{flag.message}</p>
    )}
    {flag.evidence && (
      <p className="text-[10px] text-muted-foreground italic border-l-2 border-border pl-2 mt-1">{flag.evidence}</p>
    )}
  </div>
);

// ============================================================================
// Safety Incident Row
// ============================================================================

interface SafetyIncidentRowProps {
  item: SafetyIncidentItem;
  expanded: boolean;
  onToggle: () => void;
  detail: SafetyIncidentDetail | null;
  detailLoading: boolean;
  onUpdate: () => void;
}

const SafetyIncidentRow: React.FC<SafetyIncidentRowProps> = ({
  item,
  expanded,
  onToggle,
  detail,
  detailLoading,
  onUpdate,
}) => {
  const [actionStatus, setActionStatus] = useState<SafetyIncidentStatus | ''>(item.status);
  const [actionResolution, setActionResolution] = useState<SafetyIncidentResolution | ''>(item.resolution || '');
  const [resolutionNotes, setResolutionNotes] = useState(item.resolutionNotes || '');
  const [submitting, setSubmitting] = useState(false);
  const [contacting, setContacting] = useState(false);

  // Reset action fields when a new detail loads
  React.useEffect(() => {
    if (detail) {
      setActionStatus(detail.status);
      setActionResolution(detail.resolution || '');
      setResolutionNotes(detail.resolutionNotes || '');
    }
  }, [detail]);

  const handleUpdate = async () => {
    setSubmitting(true);
    try {
      const body: UpdateSafetyIncidentBody = {};
      if (actionStatus && actionStatus !== item.status) body.status = actionStatus as SafetyIncidentStatus;
      if (actionResolution) body.resolution = actionResolution as SafetyIncidentResolution;
      if (resolutionNotes.trim()) body.resolutionNotes = resolutionNotes.trim();

      await safetyIncidentsService.update(item.id, body);
      showToast('success', 'Safety incident updated');
      onUpdate();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to update incident');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactCustomer = async () => {
    setContacting(true);
    try {
      await safetyIncidentsService.contactCustomer(item.id);
      showToast('success', 'Customer contact recorded');
      onUpdate();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to contact customer');
    } finally {
      setContacting(false);
    }
  };

  return (
    <div className={cn('card p-3 cursor-pointer transition-all overflow-hidden', expanded && 'ring-1 ring-primary/30')} onClick={onToggle}>
      {/* Collapsed row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
          <div className="flex-shrink-0 w-14 text-xs text-muted-foreground">
            {formatRelativeTime(item.incidentAt)}
          </div>
          <div className="min-w-0 max-w-[240px]">
            <p className="text-xs text-foreground font-medium truncate">
              {item.customerName || `Incident #${item.id}`}
            </p>
            <p className="text-[10px] text-muted-foreground truncate">{item.flagMessage || item.description || 'No description'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <Badge variant={categoryBadgeVariant(item.category)} size="sm">{formatCategoryLabel(item.category)}</Badge>
          <Badge variant={statusBadgeVariant(item.status)} size="sm">{formatStatusLabel(item.status)}</Badge>
          {item.providerName && (
            <span className="text-[10px] text-muted-foreground">Provider: {item.providerName}</span>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
          {detailLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
            </div>
          ) : detail ? (
            <>
              {/* Red Flags */}
              {detail.redFlags && detail.redFlags.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" />
                    Red Flags ({detail.redFlags.length})
                  </p>
                  <div className="space-y-1.5">
                    {detail.redFlags.map((flag) => (
                      <FlagCard key={flag.id} flag={flag} />
                    ))}
                  </div>
                </div>
              )}

              {/* All Flags */}
              {detail.allFlags && detail.allFlags.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    All Flags ({detail.allFlags.length})
                  </p>
                  <div className="space-y-1.5">
                    {detail.allFlags.map((flag) => (
                      <FlagCard key={flag.id} flag={flag} />
                    ))}
                  </div>
                </div>
              )}

              {/* Transcripts */}
              {detail.transcripts && detail.transcripts.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Transcript ({detail.transcripts.length} segments)
                  </p>
                  <div className="bg-background/50 border border-border rounded-lg p-2.5 max-h-48 overflow-y-auto space-y-1.5">
                    {detail.transcripts.map((seg) => (
                      <div key={seg.id} className="flex gap-2">
                        <span className="text-[10px] text-muted-foreground w-14 flex-shrink-0">{formatRelativeTime(seg.timestamp)}</span>
                        <span className="text-[10px] font-medium text-primary flex-shrink-0">{seg.speaker}:</span>
                        <span className="text-[10px] text-foreground">{seg.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Session Events */}
              {detail.sessionEvents && detail.sessionEvents.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Session Events
                  </p>
                  <div className="bg-background/50 border border-border rounded-lg p-2.5 space-y-1">
                    {detail.sessionEvents.map((evt) => (
                      <div key={evt.id} className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-14 flex-shrink-0">{formatRelativeTime(evt.timestamp)}</span>
                        <Badge variant="muted" size="sm">{evt.type}</Badge>
                        {evt.userName && <span className="text-[10px] text-foreground">{evt.userName}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recording Assets */}
              {detail.recordingAssets && detail.recordingAssets.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <Play className="h-3 w-3" />
                    Recording Assets
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {detail.recordingAssets.map((asset) => (
                      <a
                        key={asset.id}
                        href={asset.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-medium bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors border border-primary/20"
                      >
                        {asset.type === 'VIDEO' ? <Play className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                        {asset.type} {asset.duration ? `(${Math.round(asset.duration)}s)` : ''}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Booking Info */}
              {detail.booking && (
                <DetailSection title="Booking Info">
                  <DetailRow label="Booking ID" value={String(detail.booking.id)} />
                  <DetailRow label="Status" value={detail.booking.status} />
                  <DetailRow label="Scheduled" value={detail.booking.scheduledAt ? formatDateTime(detail.booking.scheduledAt) : undefined} />
                  <DetailRow label="Started" value={detail.booking.startedAt ? formatDateTime(detail.booking.startedAt) : undefined} />
                  <DetailRow label="Ended" value={detail.booking.endedAt ? formatDateTime(detail.booking.endedAt) : undefined} />
                  {detail.booking.customer && (
                    <>
                      <DetailRow label="Customer" value={
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {detail.booking.customer.name || detail.booking.customer.email || `User #${detail.booking.customer.id}`}
                        </span>
                      } />
                      <DetailRow label="Customer Email" value={detail.booking.customer.email || undefined} />
                      <DetailRow label="Customer Phone" value={detail.booking.customer.phoneNumber || undefined} />
                    </>
                  )}
                  {detail.booking.provider && (
                    <>
                      <DetailRow label="Provider" value={
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {detail.booking.provider.name || detail.booking.provider.email || `User #${detail.booking.provider.id}`}
                        </span>
                      } />
                      <DetailRow label="Provider Email" value={detail.booking.provider.email || undefined} />
                    </>
                  )}
                  {detail.booking.beneficiary && (
                    <>
                      <DetailRow label="Beneficiary" value={
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {detail.booking.beneficiary.name || detail.booking.beneficiary.email || `User #${detail.booking.beneficiary.id}`}
                        </span>
                      } />
                    </>
                  )}
                </DetailSection>
              )}

              {/* Monitoring */}
              {detail.monitoring && (
                <DetailSection title="Session Monitoring">
                  <DetailRow label="Status" value={detail.monitoring.status} />
                  <DetailRow label="Started" value={detail.monitoring.startedAt ? formatDateTime(detail.monitoring.startedAt) : undefined} />
                  <DetailRow label="Ended" value={detail.monitoring.endedAt ? formatDateTime(detail.monitoring.endedAt) : undefined} />
                  <DetailRow label="Monitored By" value={detail.monitoring.monitoredByAdminId ? `Admin #${detail.monitoring.monitoredByAdminId}` : undefined} />
                  <DetailRow label="Notes" value={detail.monitoring.notes || undefined} />
                </DetailSection>
              )}

              {/* Support Ticket */}
              {detail.supportTicket && (
                <DetailSection title="Support Ticket">
                  <DetailRow label="Ticket #" value={detail.supportTicket.ticketNumber} />
                  <DetailRow label="Status" value={detail.supportTicket.status} />
                  <DetailRow label="Subject" value={detail.supportTicket.subject || undefined} />
                  <DetailRow label="Created" value={formatDateTime(detail.supportTicket.createdAt)} />
                </DetailSection>
              )}

              {/* Incident Metadata */}
              <DetailSection title="Incident Details">
                <DetailRow label="ID" value={String(detail.id)} />
                <DetailRow label="Session ID" value={detail.sessionId ? String(detail.sessionId) : undefined} />
                <DetailRow label="Booking ID" value={detail.bookingId ? String(detail.bookingId) : undefined} />
                <DetailRow label="Assigned To" value={detail.assignedToAdminId ? `Admin #${detail.assignedToAdminId}` : 'Unassigned'} />
                <DetailRow label="Resolution" value={detail.resolution ? formatStatusLabel(detail.resolution) : undefined} />
                <DetailRow label="Resolved By" value={detail.resolvedByAdminId ? `Admin #${detail.resolvedByAdminId}` : undefined} />
                <DetailRow label="Resolved At" value={detail.resolvedAt ? formatDateTime(detail.resolvedAt) : undefined} />
                <DetailRow label="Customer Contacted" value={detail.customerContactedAt ? formatDateTime(detail.customerContactedAt) : 'Not yet'} />
                <DetailRow label="Incident At" value={formatDateTime(detail.incidentAt)} />
                <DetailRow label="Created" value={formatDateTime(detail.createdAt)} />
                <DetailRow label="Updated" value={formatDateTime(detail.updatedAt)} />
              </DetailSection>

              {/* Resolution Notes (read-only display if already set) */}
              {detail.resolutionNotes && (
                <div className="pt-2 mt-2">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Resolution Notes</p>
                  <p className="text-[11px] text-foreground bg-background/50 border border-border rounded-lg p-2.5">{detail.resolutionNotes}</p>
                </div>
              )}

              {/* Actions Panel */}
              <div className="pt-3 mt-3 border-t border-border space-y-3">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Actions</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Status */}
                  <div>
                    <label className="block text-[10px] text-muted-foreground mb-1">Status</label>
                    <select
                      value={actionStatus}
                      onChange={(e) => setActionStatus(e.target.value as SafetyIncidentStatus)}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      {STATUS_OPTIONS.filter((o) => o.value !== '').map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Resolution */}
                  <div>
                    <label className="block text-[10px] text-muted-foreground mb-1">Resolution</label>
                    <select
                      value={actionResolution}
                      onChange={(e) => setActionResolution(e.target.value as SafetyIncidentResolution | '')}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      {RESOLUTION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Resolution Notes */}
                <div>
                  <label className="block text-[10px] text-muted-foreground mb-1">Resolution Notes</label>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Add notes about the resolution..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleContactCustomer}
                    disabled={contacting}
                    className="px-3 py-2 text-xs font-medium rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {contacting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />}
                    Contact Customer
                  </button>

                  <button
                    onClick={handleUpdate}
                    disabled={submitting}
                    className="px-3 py-2 text-xs font-medium rounded-lg bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Update Incident
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-xs text-muted-foreground">Failed to load incident details</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Page
// ============================================================================

const SafetyIncidents: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<SafetyIncidentStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<SafetyIncidentCategory | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<{ id: number; data: SafetyIncidentDetail | null; loading: boolean } | null>(null);

  const listParams = useMemo(() => ({
    status: statusFilter || undefined,
    category: categoryFilter || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    page,
    limit: PAGE_SIZE,
  }), [statusFilter, categoryFilter, startDate, endDate, page]);

  const { data: stats, loading: statsLoading, refetch: refetchStats } = useSafetyIncidentStats();
  const { items, total, loading, error, page: currentPage, totalPages, refetch: refetchList } = useSafetyIncidents(listParams);

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchStats(), refetchList()]);
  }, [refetchStats, refetchList]);

  const handleToggleExpand = useCallback(async (item: SafetyIncidentItem) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(item.id);
    setExpandedDetail({ id: item.id, data: null, loading: true });
    try {
      const data = await safetyIncidentsService.getDetail(item.id);
      setExpandedDetail({ id: item.id, data, loading: false });
    } catch {
      setExpandedDetail({ id: item.id, data: null, loading: false });
    }
  }, [expandedId]);

  const handleUpdate = useCallback(() => {
    setExpandedId(null);
    setExpandedDetail(null);
    refetchAll();
  }, [refetchAll]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-foreground">Safety Incidents</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Monitor and manage session safety incidents</p>
            </div>
            <button
              onClick={() => refetchAll()}
              disabled={loading || statsLoading}
              className="p-2 rounded-lg hover:bg-primary/10 transition-colors"
            >
              <RefreshCw className={cn('h-4 w-4 text-muted-foreground', (loading || statsLoading) && 'animate-spin')} />
            </button>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <StatCard label="Open" value={stats ? formatNumber(stats.open) : '--'} icon={AlertTriangle} color="text-red-400" bgColor="bg-red-400/10" loading={statsLoading} />
            <StatCard label="Under Review" value={stats ? formatNumber(stats.under_review) : '--'} icon={Eye} color="text-blue-400" bgColor="bg-blue-400/10" loading={statsLoading} />
            <StatCard label="Confirmed" value={stats ? formatNumber(stats.confirmed) : '--'} icon={ShieldAlert} color="text-orange-400" bgColor="bg-orange-400/10" loading={statsLoading} />
            <StatCard label="Escalated" value={stats ? formatNumber(stats.escalated) : '--'} icon={AlertOctagon} color="text-red-600" bgColor="bg-red-600/10" loading={statsLoading} />
            <StatCard label="Total" value={stats ? formatNumber(stats.total) : '--'} icon={FileText} color="text-purple-400" bgColor="bg-purple-400/10" loading={statsLoading} />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as SafetyIncidentStatus | ''); setPage(1); }}
              className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value as SafetyIncidentCategory | ''); setPage(1); }}
              className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            >
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              placeholder="Start date"
              className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              placeholder="End date"
              className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
            <span className="text-xs text-muted-foreground">{formatNumber(total)} incidents</span>
          </div>

          {/* Error state */}
          {error && (
            <div className="card p-4 border-red-500/30">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="h-4 w-4" />
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* List */}
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="card p-12 text-center">
              <ShieldAlert className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No safety incidents found</p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                {statusFilter || categoryFilter ? 'Try changing the filters' : 'No incidents have been reported yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <SafetyIncidentRow
                  key={item.id}
                  item={item}
                  expanded={expandedId === item.id}
                  onToggle={() => handleToggleExpand(item)}
                  detail={expandedDetail?.id === item.id ? expandedDetail.data : null}
                  detailLoading={expandedDetail?.id === item.id ? expandedDetail.loading : false}
                  onUpdate={handleUpdate}
                />
              ))}
            </div>
          )}

          <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default SafetyIncidents;
